// Refund policy widget: data-365="refund-policy"
// Shows the venue's refund rules exactly as the platform enforces them. The text is GENERATED server-side
// from the venue's settings (365 Admin -> Refund Policy), so the venue site can never promise something the
// system does not do. Anonymous, keyed by the venue's publishable key:
//   GET /api/embed/{key}/refund-policy
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, clear, footer, renderLoading } from '../ui';
import { handleFetchFailure } from '../maintenance';

interface PolicySection { key: string; title: string; sentences: string[]; }
interface PublicRefundPolicy {
  venueName: string;
  venueSlug: string;
  sections: PolicySection[];
  venueNote?: string | null;
  updatedAt?: string | null;
  // Which request paths the venue actually has (API 2026-10-27+; absent on an older API = assume yes).
  bookingRequestsAllowed?: boolean;
  streamRequestsAllowed?: boolean;
  sellsBookings?: boolean;
  sellsStreams?: boolean;
}

const TITLE = 'Refund policy';

const CSS = `
.e365 .e365-pol h4 { margin: 14px 0 4px; font-size: 1rem; }
.e365 .e365-pol p { margin: 0 0 6px; font-size: .92rem; line-height: 1.5; }
.e365 .e365-pol-note { white-space: pre-line; border-left: 3px solid var(--e365-primary); padding: 8px 12px; margin-top: 8px; background: var(--e365-surface); border-radius: 6px; }
.e365 .e365-pol-upd { color: var(--e365-muted); font-size: .78rem; margin-top: 10px; }
`;

function updatedLabel(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `Last updated ${d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

export function mountRefundPolicy(mount: HTMLElement, cfg: EmbedConfig, theme: ThemeOptions, venueKey: string): void {
  const { root, shadow } = createRoot(mount, theme);
  const style = document.createElement('style');
  style.textContent = CSS;
  shadow.insertBefore(style, root);

  const url = `${cfg.baseApiUrl}/api/embed/${encodeURIComponent(venueKey)}/refund-policy`;

  async function load(): Promise<void> {
    renderLoading(root, TITLE);
    let policy: PublicRefundPolicy;
    try {
      let res: Response;
      try { res = await fetch(url); }
      catch (e) { const down = await handleFetchFailure(null); if (down) return; throw e; }
      if (res.status === 503 && await handleFetchFailure(res)) return;
      if (!res.ok) throw new Error(String(res.status));
      policy = (await res.json()) as PublicRefundPolicy;
    } catch {
      clear(root);
      root.append(
        h('h3', undefined, TITLE),
        h('div', { class: 'e365-alert error' }, 'We couldn’t load the refund policy just now.'),
      );
      const retry = h('button', { class: 'e365-btn', type: 'button' }, 'Try again');
      retry.addEventListener('click', () => void load());
      root.append(retry, footer());
      return;
    }

    clear(root);
    const box = h('div', { class: 'e365-pol' });
    box.append(h('h3', undefined, `${policy.venueName} refund policy`));
    box.append(h('p', { class: 'e365-muted' },
      'These rules are applied automatically to what you buy here, and they are the rules in force when you bought.'));
    for (const s of policy.sections) {
      box.append(h('h4', undefined, s.title));
      for (const line of s.sentences) box.append(h('p', undefined, line));
    }
    if (policy.venueNote) {
      box.append(h('h4', undefined, `A note from ${policy.venueName}`));
      box.append(h('div', { class: 'e365-pol-note' }, policy.venueNote));
    }
    // Only the request paths this venue has: never point at a Request Refund button it switched off.
    const askBooking = policy.sellsBookings !== false && policy.bookingRequestsAllowed !== false;
    const askStream = policy.sellsStreams !== false && policy.streamRequestsAllowed !== false;
    if (askBooking || askStream) {
      const where = [
        askBooking ? 'Bookings: open My Bookings and use Request Refund on the order.' : '',
        askStream ? 'Streams and recordings: My Streams.' : '',
      ].filter(Boolean).join(' ');
      box.append(h('h4', undefined, 'How to ask'));
      box.append(h('p', undefined,
        `${where} ${policy.venueName} reviews the request and emails you its decision. Approved refunds go back to the card you paid with, usually within 5 to 10 business days.`));
    }
    const upd = updatedLabel(policy.updatedAt);
    if (upd) box.append(h('div', { class: 'e365-pol-upd' }, upd));
    root.append(box, footer());
  }

  void load();
}
